import { useNavigate, useLocation } from 'react-router-dom';
import { AlmaOrb } from './AlmaOrb';
import { Drawer } from './Drawer';
import { ExportButton } from './ExportButton';
import { FOCUS_RING_CLASSES } from '../lib/focusTrap';
import { isNavActive, PRIMARY_NAV, SECONDARY_NAV } from '../lib/nav';

/** Minimal user shape for SideNav (profile display only). Parent may pass a fuller User. */
interface SideNavUser {
  email: string;
  displayName?: string;
}

interface SideNavProps {
  isOpen: boolean;
  onClose: () => void;
  user?: SideNavUser | null;
}

/** Mobile slide-out navigation with nav links and user profile. */
export function SideNav({ isOpen, onClose, user }: SideNavProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  const displayName = user?.displayName || user?.email?.split('@')[0] || '?';
  const initial = (user?.displayName || user?.email || '?').charAt(0).toUpperCase();

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel="Navigation menu"
      closeLabel="Close menu"
      overlayTestId="sidenav-overlay"
      headerContent={<AlmaOrb size="sm" />}
    >
      <nav className="flex-1 p-4 overflow-y-auto overscroll-contain" aria-label="Main navigation">
        <div className="space-y-1">
          {PRIMARY_NAV.map((item) => {
            const Icon = item.icon;
            const isActive = isNavActive(item, location.pathname);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNavigate(item.path)}
                className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all ${FOCUS_RING_CLASSES} ${isActive ? 'bg-gradient-to-r from-[var(--marine-600)] to-[var(--marine-400)] text-white shadow-lg' : 'text-[var(--text-secondary)] hover:bg-[color-mix(in_oklab,var(--text-primary)_5%,transparent)]'}`}
              >
                <Icon className="w-5 h-5 shrink-0" aria-hidden />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
        <p className="pt-5 pb-1 px-4 text-xs font-medium uppercase tracking-wide text-[var(--text-tertiary)]">
          Library
        </p>
        <div className="space-y-1">
          {SECONDARY_NAV.map((item) => {
            const Icon = item.icon;
            const isActive = isNavActive(item, location.pathname);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNavigate(item.path)}
                className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all ${FOCUS_RING_CLASSES} ${isActive ? 'bg-gradient-to-r from-[var(--marine-600)] to-[var(--marine-400)] text-white shadow-lg' : 'text-[var(--text-secondary)] hover:bg-[color-mix(in_oklab,var(--text-primary)_5%,transparent)]'}`}
              >
                <Icon className="w-5 h-5 shrink-0" aria-hidden />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <div className="p-4 border-t border-[var(--card-border)] space-y-1">
        <button
          type="button"
          onClick={() => handleNavigate('/profile')}
          className={`w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-[color-mix(in_oklab,var(--text-primary)_5%,transparent)] transition-all text-left ${FOCUS_RING_CLASSES}`}
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--marine-400)] to-[var(--marine-600)] flex items-center justify-center text-white font-bold shrink-0">
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-[var(--text-primary)] truncate">{displayName}</p>
            <p className="text-xs text-[var(--text-tertiary)]">View profile</p>
          </div>
        </button>

        <ExportButton />
      </div>
    </Drawer>
  );
}
